/**
 * ===================================================================
 * FRONTEND SERVICE - RESERVOIRS
 * ===================================================================
 * API sluzba pro spravu nadrzi.
 */

import { apiClient } from './api';
import { Reservoir, CreateReservoirDTO, UpdateReservoirDTO } from '../types/reservoirs';
import type { SensorData } from '../types/sensors';
import type {
  ReservoirEvent,
  CreateReservoirEventDTO,
  ReservoirEventsResponse,
} from '../types/reservoir-events';

export const reservoirService = {
  // Ziskej vsechny nadrze pro zarizeni
  getDeviceReservoirs: async (deviceUuid: string): Promise<Reservoir[]> => {
    return apiClient.get<Reservoir[]>(`/reservoirs/device/${deviceUuid}`);
  },

  // Ziskej vychozi nadrz pro zarizeni
  getDefaultReservoir: async (deviceUuid: string): Promise<Reservoir> => {
    return apiClient.get<Reservoir>(`/reservoirs/device/${deviceUuid}/default`);
  },

  // Ziskej jednu nadrz
  getReservoir: async (reservoirId: string): Promise<Reservoir> => {
    return apiClient.get<Reservoir>(`/reservoirs/${reservoirId}`);
  },

  // Vytvor novou nadrz
  createReservoir: async (data: CreateReservoirDTO): Promise<Reservoir> => {
    return apiClient.post<Reservoir>('/reservoirs', data as unknown as Record<string, unknown>);
  },

  // Aktualizuj nadrz
  updateReservoir: async (reservoirId: string, data: UpdateReservoirDTO): Promise<Reservoir> => {
    return apiClient.patch<Reservoir>(
      `/reservoirs/${reservoirId}`,
      data as unknown as Record<string, unknown>,
    );
  },

  // Smaz nadrz
  deleteReservoir: async (reservoirId: string): Promise<void> => {
    return apiClient.delete(`/reservoirs/${reservoirId}`);
  },

  // Ziskej senzory prirazene k nadrzi
  getReservoirSensors: async (reservoirId: string): Promise<SensorData[]> => {
    return apiClient.get<SensorData[]>(`/reservoirs/${reservoirId}/sensors`);
  },

  // Prirad senzor k nadrzi
  assignSensor: async (reservoirId: string, sensorId: string): Promise<SensorData> => {
    return apiClient.post<SensorData>(`/reservoirs/${reservoirId}/sensors/${sensorId}`, {});
  },

  // Odeber senzor z nadrze
  unassignSensor: async (reservoirId: string, sensorId: string): Promise<SensorData> => {
    return apiClient.delete(`/reservoirs/${reservoirId}/sensors/${sensorId}`);
  },

  // ===== Reservoir Events =====

  // Ziskej udalosti nadrze
  getEvents: async (
    reservoirId: string,
    limit = 50,
    offset = 0,
  ): Promise<ReservoirEventsResponse> => {
    return apiClient.get<ReservoirEventsResponse>(
      `/reservoirs/${reservoirId}/events?limit=${limit}&offset=${offset}`,
    );
  },

  // Vytvor novou udalost
  createEvent: async (
    reservoirId: string,
    data: CreateReservoirEventDTO,
  ): Promise<ReservoirEvent> => {
    return apiClient.post<ReservoirEvent>(
      `/reservoirs/${reservoirId}/events`,
      data as unknown as Record<string, unknown>,
    );
  },

  // Smaz udalost
  deleteEvent: async (reservoirId: string, eventId: string): Promise<void> => {
    return apiClient.delete(`/reservoirs/${reservoirId}/events/${eventId}`);
  },
};
